import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronLeft, Lock, Loader2, ShieldCheck } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";
import Navbar from "./Navbar";
import Footer from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gbp = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n) || 0);

const inputCls =
  "w-full bg-white border border-[#d9c3a8] px-4 py-3 rounded-sm outline-none focus:ring-2 focus:ring-[#ebc754] text-[#1e231f]";

const Label = ({ children }) => (
  <span className="block text-xs font-bold tracking-[0.12em] text-[#7a6a58] uppercase mb-1.5">
    {children}
  </span>
);

const PHONE_COUNTRIES = [
  { iso: "GB", flag: "🇬🇧", name: "United Kingdom", dial: "+44" },
  { iso: "IE", flag: "🇮🇪", name: "Ireland", dial: "+353" },
  { iso: "US", flag: "🇺🇸", name: "United States", dial: "+1" },
  { iso: "CA", flag: "🇨🇦", name: "Canada", dial: "+1" },
  { iso: "AU", flag: "🇦🇺", name: "Australia", dial: "+61" },
  { iso: "NZ", flag: "🇳🇿", name: "New Zealand", dial: "+64" },
  { iso: "FR", flag: "🇫🇷", name: "France", dial: "+33" },
  { iso: "DE", flag: "🇩🇪", name: "Germany", dial: "+49" },
  { iso: "ES", flag: "🇪🇸", name: "Spain", dial: "+34" },
  { iso: "IT", flag: "🇮🇹", name: "Italy", dial: "+39" },
  { iso: "PT", flag: "🇵🇹", name: "Portugal", dial: "+351" },
  { iso: "NL", flag: "🇳🇱", name: "Netherlands", dial: "+31" },
  { iso: "BE", flag: "🇧🇪", name: "Belgium", dial: "+32" },
  { iso: "CH", flag: "🇨🇭", name: "Switzerland", dial: "+41" },
  { iso: "AT", flag: "🇦🇹", name: "Austria", dial: "+43" },
  { iso: "DK", flag: "🇩🇰", name: "Denmark", dial: "+45" },
  { iso: "SE", flag: "🇸🇪", name: "Sweden", dial: "+46" },
  { iso: "NO", flag: "🇳🇴", name: "Norway", dial: "+47" },
  { iso: "FI", flag: "🇫🇮", name: "Finland", dial: "+358" },
  { iso: "PL", flag: "🇵🇱", name: "Poland", dial: "+48" },
  { iso: "CZ", flag: "🇨🇿", name: "Czechia", dial: "+420" },
  { iso: "GR", flag: "🇬🇷", name: "Greece", dial: "+30" },
  { iso: "TR", flag: "🇹🇷", name: "Türkiye", dial: "+90" },
  { iso: "AE", flag: "🇦🇪", name: "United Arab Emirates", dial: "+971" },
  { iso: "IN", flag: "🇮🇳", name: "India", dial: "+91" },
  { iso: "ZA", flag: "🇿🇦", name: "South Africa", dial: "+27" },
  { iso: "SG", flag: "🇸🇬", name: "Singapore", dial: "+65" },
  { iso: "HK", flag: "🇭🇰", name: "Hong Kong", dial: "+852" },
  { iso: "JP", flag: "🇯🇵", name: "Japan", dial: "+81" },
  { iso: "KR", flag: "🇰🇷", name: "South Korea", dial: "+82" },
  { iso: "CN", flag: "🇨🇳", name: "China", dial: "+86" },
  { iso: "PH", flag: "🇵🇭", name: "Philippines", dial: "+63" },
  { iso: "BR", flag: "🇧🇷", name: "Brazil", dial: "+55" },
  { iso: "MX", flag: "🇲🇽", name: "Mexico", dial: "+52" },
];


const CountryFlag = ({ iso, name }) => (
  <img
    src={`https://flagcdn.com/24x18/${iso.toLowerCase()}.png`}
    srcSet={`https://flagcdn.com/48x36/${iso.toLowerCase()}.png 2x`}
    width="24"
    height="18"
    alt={`${name} flag`}
    className="block w-6 h-[18px] object-cover rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
  />
);

const normalisePhone = (countryIso, localNumber) => {
  const country =
    PHONE_COUNTRIES.find((item) => item.iso === countryIso) || PHONE_COUNTRIES[0];
  const localDigits = (localNumber || "").replace(/\D/g, "").replace(/^0+/, "");
  return localDigits ? `${country.dial}${localDigits}` : "";
};

const appearance = {
  theme: "flat",
  variables: {
    colorPrimary: "#9b3d0a",
    colorBackground: "#ffffff",
    colorText: "#1e231f",
    colorDanger: "#b3261e",
    fontFamily: "inherit",
    borderRadius: "2px",
    spacingUnit: "4px",
  },
  rules: {
    ".Input": {
      border: "1px solid #d9c3a8",
      padding: "12px",
      boxShadow: "none",
    },
    ".Input:focus": {
      border: "1px solid #ebc754",
      boxShadow: "0 0 0 2px rgba(206,138,57,0.35)",
    },
    ".Label": {
      fontSize: "12px",
      fontWeight: "700",
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: "#7a6a58",
    },
  },
};

/* ---- Inner form: has access to Stripe Elements ---- */
const PaymentForm = ({
  paymentIntentId,
  total,
  form,
  onChange,
  showReg,
  showDietary,
  lines = [],
  bookingConfig = {},
  payMode = "full",
  firstAmount = 0,
  installmentAmount = 0,
  installmentCount = 4,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [phoneCountryOpen, setPhoneCountryOpen] = useState(false);
  const phoneCountryRef = useRef(null);

  const selectedPhoneCountry =
    PHONE_COUNTRIES.find((country) => country.iso === (form.phoneCountry || "GB")) ||
    PHONE_COUNTRIES[0];

  useEffect(() => {
    if (!phoneCountryOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (phoneCountryRef.current && !phoneCountryRef.current.contains(event.target)) {
        setPhoneCountryOpen(false);
      }
    };

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setPhoneCountryOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [phoneCountryOpen]);

  const { askHeard = false, bedChoiceIds = [], guestPlaceIds = [] } = bookingConfig;
  const bedUnits = [];
  lines
    .filter((l) => bedChoiceIds.includes(l.id))
    .forEach((l) => {
      for (let u = 0; u < l.qty; u++) {
        bedUnits.push({ key: `${l.id}::${u}`, label: l.qty > 1 ? `${l.name} (room ${u + 1})` : l.name });
      }
    });
  const placeCount = lines
    .filter((l) => guestPlaceIds.includes(l.id))
    .reduce((s, l) => s + l.qty, 0);
  const extraGuests = Math.max(placeCount - 1, 0);
  const beds = form.beds || {};
  const guests = form.guests || [];

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) {
      setError("Please enter your full name.");
      return;
    }

    if (
      !form.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    ) {
      setError("Please enter a valid email address.");
      return;
    }

    const phoneValue = normalisePhone(form.phoneCountry || "GB", form.phone);
    const phoneDigits = phoneValue.replace(/\D/g, "");

    if (phoneDigits.length < 8) {
      setError(
        "A valid contact phone number is required to complete your booking."
      );
      return;
    }

    if (askHeard && !(form.heardAbout || "").trim()) {
      setError("Please let us know where you heard about us.");
      return;
    }

    for (const b of bedUnits) {
      if (!beds[b.key]) {
        setError(`Please choose twin or double for ${b.label}.`);
        return;
      }
    }

    for (let i = 0; i < extraGuests; i++) {
      const g = guests[i] || {};
      if (!(g.name || "").trim()) {
        setError(`Please enter the full name for guest ${i + 2}.`);
        return;
      }
      if (!(g.email || "").trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(g.email.trim())) {
        setError(`Please enter a valid email for guest ${i + 2}.`);
        return;
      }
      if ((g.phone || "").replace(/\D/g, "").length < 7) {
        setError(`Please enter a valid phone number for guest ${i + 2}.`);
        return;
      }
    }

    if (!stripe || !elements) return;

    setSubmitting(true);

    try {
      await axios.post(`${API}/payments/attach-customer`, {
        payment_intent_id: paymentIntentId,
        customer: {
          name: form.name,
          email: form.email,
          phone: phoneValue,
          reg: form.reg || "",
          dietary: form.dietary || "",
          heardAbout: form.heardAbout || "",
          bedChoices: bedUnits.map((b) => ({ room: b.label, bed: beds[b.key] || "" })),
          guests: guests.slice(0, extraGuests).map((g) => ({
            name: g.name || "",
            email: g.email || "",
            phone: g.phone || "",
          })),
        },
      });
    } catch (_) {
      /* non-fatal; continue to payment */
    }

    const { error: submitErr } = await elements.submit();

    if (submitErr) {
      setSubmitting(false);
      setError(
        submitErr.message || "Please check your card details."
      );
      return;
    }

    const { error: payErr, paymentIntent } =
      await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/success`,
          payment_method_data: {
            billing_details: {
              name: form.name,
              email: form.email,
              phone: phoneValue,
            },
          },
        },
        redirect: "if_required",
      });

    if (payErr) {
      setSubmitting(false);
      setError(
        payErr.message ||
          "Your payment could not be completed. Please try again."
      );
      return;
    }

    if (
      paymentIntent &&
      paymentIntent.status === "succeeded"
    ) {
      navigate(
        `/payment/success?payment_intent=${paymentIntent.id}`
      );
    } else {
      setSubmitting(false);
      setError(
        "Payment is still processing. Please wait a moment and try again."
      );
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white border border-[#d9c3a8] rounded-md p-7 md:p-8 shadow-sm"
    >
      <h2 className="text-[#b5651d] tracking-[0.14em] text-sm font-bold uppercase mb-6">
        Your details
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <label className="block md:col-span-2">
          <Label>Full name</Label>
          <input
            className={inputCls}
            data-testid="checkout-name"
            value={form.name}
            onChange={(e) =>
              onChange({
                ...form,
                name: e.target.value,
              })
            }
            placeholder="Your name"
          />
        </label>

        <label className="block">
          <Label>Email</Label>
          <input
            type="email"
            className={inputCls}
            data-testid="checkout-email"
            value={form.email}
            onChange={(e) =>
              onChange({
                ...form,
                email: e.target.value,
              })
            }
            placeholder="email@site.com"
          />
          <span className="block text-xs text-[#9a876f] mt-1.5">
            We'll email your order details and ticket information here.
          </span>
        </label>

        <div className="block">
          <Label>Phone number *</Label>
          <div className="flex rounded-sm focus-within:ring-2 focus-within:ring-[#ebc754]">
            <div className="relative shrink-0" ref={phoneCountryRef}>
              <button
                type="button"
                onClick={() => setPhoneCountryOpen((open) => !open)}
                data-testid="checkout-phone-country"
                aria-label="Phone country code"
                aria-haspopup="listbox"
                aria-expanded={phoneCountryOpen}
                className="h-full min-h-[50px] w-[116px] bg-[#f2e5de] border border-r-0 border-[#d9c3a8] px-3 rounded-l-sm outline-none text-[#1e231f] text-sm flex items-center justify-between gap-2"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <CountryFlag
                    iso={selectedPhoneCountry.iso}
                    name={selectedPhoneCountry.name}
                  />
                  <span className="font-medium">{selectedPhoneCountry.dial}</span>
                </span>
                <ChevronDown
                  size={15}
                  className={`shrink-0 text-[#7a6a58] transition-transform ${
                    phoneCountryOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {phoneCountryOpen && (
                <div
                  role="listbox"
                  aria-label="Choose phone country"
                  className="absolute z-50 top-[calc(100%+6px)] left-0 w-[290px] max-h-72 overflow-y-auto bg-white border border-[#d9c3a8] rounded-sm shadow-xl py-1"
                >
                  {PHONE_COUNTRIES.map((country) => (
                    <button
                      key={country.iso}
                      type="button"
                      role="option"
                      aria-selected={country.iso === selectedPhoneCountry.iso}
                      onClick={() => {
                        onChange({
                          ...form,
                          phoneCountry: country.iso,
                        });
                        setPhoneCountryOpen(false);
                      }}
                      className={`w-full px-3 py-2.5 flex items-center gap-3 text-left hover:bg-[#f2e5de] transition-colors ${
                        country.iso === selectedPhoneCountry.iso ? "bg-[#f3e7d6]" : ""
                      }`}
                    >
                      <CountryFlag iso={country.iso} name={country.name} />
                      <span className="min-w-0 flex-1 text-sm text-[#1e231f] truncate">
                        {country.name}
                      </span>
                      <span className="text-sm font-semibold text-[#7a6a58]">
                        {country.dial}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              type="tel"
              className="min-w-0 flex-1 bg-white border border-[#d9c3a8] px-4 py-3 rounded-r-sm outline-none text-[#1e231f]"
              data-testid="checkout-phone"
              value={form.phone || ""}
              onChange={(e) =>
                onChange({
                  ...form,
                  phone: e.target.value,
                })
              }
              placeholder="7912 345678"
              autoComplete="tel-national"
            />
          </div>
          <span className="block text-xs text-[#9a876f] mt-1.5">
            UK +44 is selected by default. Choose another country if needed.
          </span>
        </div>

        {showReg && (
          <label className="block md:col-span-2">
            <Label>Vehicle registration</Label>
            <input
              className={inputCls}
              data-testid="checkout-reg"
              value={form.reg || ""}
              onChange={(e) =>
                onChange({
                  ...form,
                  reg: e.target.value,
                })
              }
              placeholder="e.g. AB12 CDE"
            />
          </label>
        )}
        {showDietary && (
          <label className="block md:col-span-2">
            <Label>Dietary requirements or allergies</Label>
            <textarea
              className={`${inputCls} min-h-[80px] resize-y`}
              data-testid="checkout-dietary"
              value={form.dietary || ""}
              onChange={(e) =>
                onChange({
                  ...form,
                  dietary: e.target.value,
                })
              }
              placeholder="e.g. vegetarian, gluten-free, nut allergy — or leave blank if none"
            />
          </label>
        )}
      </div>

      {bedUnits.length > 0 && (
        <div className="mt-6 space-y-4" data-testid="checkout-bed-choices">
          {bedUnits.map((b) => (
            <div key={b.key}>
              <Label>{b.label} — bed preference</Label>
              <div className="flex gap-3 mt-1">
                {["Twin", "Double"].map((opt) => {
                  const val = opt.toLowerCase();
                  const active = beds[b.key] === val;
                  return (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`bed-${b.key}-${val}`}
                      onClick={() => onChange({ ...form, beds: { ...beds, [b.key]: val } })}
                      className={`flex-1 py-2.5 rounded-md border text-sm font-semibold transition-all ${
                        active ? "border-[#9b3d0a] bg-[#9b3d0a] text-[#f2e5de]" : "border-[#d9c3a8] bg-white text-[#6e5646] hover:border-[#9b3d0a]"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {askHeard && (
        <label className="block mt-6">
          <Label>Where did you hear about us?</Label>
          <input
            className={inputCls}
            data-testid="checkout-heard"
            value={form.heardAbout || ""}
            onChange={(e) => onChange({ ...form, heardAbout: e.target.value })}
            placeholder="e.g. Instagram, a friend, at FERAL Fest…"
          />
        </label>
      )}

      {extraGuests > 0 && (
        <div className="mt-8" data-testid="checkout-guests">
          <h2 className="text-[#b5651d] tracking-[0.14em] text-sm font-bold uppercase mb-1">Guest details</h2>
          <p className="text-[#6e5646] text-sm mb-4">You're booking {placeCount} places. Please add the details of your other {extraGuests} guest{extraGuests > 1 ? "s" : ""}.</p>
          <div className="space-y-5">
            {Array.from({ length: extraGuests }).map((_, i) => {
              const g = guests[i] || {};
              const setG = (patch) => {
                const next = [...guests];
                next[i] = { ...g, ...patch };
                onChange({ ...form, guests: next });
              };
              return (
                <div key={i} className="border border-[#e0d3bf] rounded-md p-4 bg-white/60">
                  <p className="text-xs font-bold tracking-[0.1em] text-[#7a6a58] uppercase mb-3">Guest {i + 2}</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input className={inputCls} data-testid={`guest-${i}-name`} value={g.name || ""} onChange={(e) => setG({ name: e.target.value })} placeholder="Full name" />
                    <input className={inputCls} data-testid={`guest-${i}-email`} value={g.email || ""} onChange={(e) => setG({ email: e.target.value })} placeholder="Email" />
                    <input className={inputCls} data-testid={`guest-${i}-phone`} value={g.phone || ""} onChange={(e) => setG({ phone: e.target.value })} placeholder="Phone number" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <h2 className="text-[#b5651d] tracking-[0.14em] text-sm font-bold uppercase mt-8 mb-4 flex items-center gap-2">
        <ShieldCheck size={16} /> Payment
      </h2>

      <div data-testid="checkout-payment-element">
        <PaymentElement
          options={{
            layout: "tabs",
            wallets: {
              link: "never",
              applePay: "never",
              googlePay: "never",
            },
            fields: {
              billingDetails: {
                name: "never",
                email: "never",
                phone: "never",
              },
            },
          }}
        />
      </div>

      {error && (
        <p
          data-testid="checkout-error"
          className="mt-5 text-sm text-[#b3261e] bg-[#fbe9e7] border border-[#f3c2bb] rounded-sm px-4 py-3"
        >
          {error}
        </p>
      )}

      {payMode === "plan" && (
        <p className="mt-5 text-sm text-[#5b4a37] bg-[#f3e7d6] border border-[#e0c9ad] rounded-sm px-4 py-3" data-testid="checkout-plan-note">
          You'll pay <strong>{gbp(firstAmount)}</strong> today. The remaining{" "}
          <strong>{installmentCount - 1}</strong> payments of{" "}
          <strong>{gbp(installmentAmount)}</strong> will be taken automatically
          from this card, one month apart.
        </p>
      )}

      <button
        type="submit"
        disabled={submitting || !stripe}
        data-testid="checkout-pay-button"
        className="w-full mt-7 bg-[#9b3d0a] hover:bg-[#7e3208] disabled:opacity-60 text-[#f2e5de] py-4 rounded-full tracking-[0.14em] text-sm font-semibold transition-colors duration-300 inline-flex items-center justify-center gap-2"
      >
        {submitting ? (
          <>
            <Loader2
              size={16}
              className="animate-spin"
            />
            PROCESSING PAYMENT…
          </>
        ) : (
          <>
            <Lock size={15} />
            {payMode === "plan"
              ? `PAY ${gbp(firstAmount)} TODAY`
              : `PAY ${gbp(total)}`}
          </>
        )}
      </button>

      <p className="text-center text-xs text-[#9a876f] mt-4">
        Payments are securely processed by Stripe. Your card details never
        touch our servers.
      </p>
    </form>
  );
};

/* ---- Outer view: loads Stripe + creates the PaymentIntent ---- */
const CheckoutView = ({
  event,
  items = [],
  donation = 0,
  eventName,
  dateLine,
  lines = [],
  total,
  showReg = false,
  showDietary = false,
  bookingConfig = {},
  onBack,
  allowPromo = true,
  allowInstallments = false,
}) => {
  const [stripePromise, setStripePromise] =
    useState(null);

  const [clientSecret, setClientSecret] =
    useState("");

  const [paymentIntentId, setPaymentIntentId] =
    useState("");

  const [status, setStatus] =
    useState("loading");

  const [errMsg, setErrMsg] =
    useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    phoneCountry: "GB",
    reg: "",
    dietary: "",
    heardAbout: "",
    beds: {},
    guests: [],
  });

  const [checkoutSubtotal, setCheckoutSubtotal] =
    useState(Number(total) || 0);

  const [checkoutTotal, setCheckoutTotal] =
    useState(Number(total) || 0);

  const [discount, setDiscount] =
    useState(0);

  const [promoInput, setPromoInput] =
    useState("");

  const [appliedPromo, setAppliedPromo] =
    useState("");

  const [promoType, setPromoType] = useState("");
  const [promoValue, setPromoValue] = useState(0);

  const [promoError, setPromoError] =
    useState("");

  const [promoMessage, setPromoMessage] =
    useState("");

  const [applyingPromo, setApplyingPromo] =
    useState(false);

  const [payMode, setPayMode] = useState("full");
  const [installmentAmount, setInstallmentAmount] = useState(0);
  const [firstAmount, setFirstAmount] = useState(0);
  const [installmentCount, setInstallmentCount] = useState(4);

  const started = useRef(false);

  const initIntent = async (mode) => {
    setStatus("loading");
    setClientSecret("");
    setAppliedPromo("");
    setPromoType("");
    setPromoValue(0);
    setPromoInput("");
    setPromoMessage("");
    setPromoError("");
    try {
      if (!stripePromise) {
        const cfg = await axios.get(`${API}/payments/config`);
        const pk = cfg.data.publishable_key;
        if (!pk) throw new Error("Payments are not configured.");
        setStripePromise(loadStripe(pk));
      }

      const endpoint =
        mode === "plan"
          ? `${API}/payments/create-installment-intent`
          : `${API}/payments/create-intent`;

      const body =
        mode === "plan"
          ? { event, items }
          : { event, items, donation };

      const { data } = await axios.post(endpoint, body);

      setClientSecret(data.client_secret);
      setPaymentIntentId(data.payment_intent_id);

      const backendSubtotal =
        Number(data.subtotal ?? data.amount ?? total) || 0;
      const backendTotal = Number(data.amount ?? total) || 0;

      setCheckoutSubtotal(backendSubtotal);
      setCheckoutTotal(backendTotal);
      setDiscount(Number(data.discount) || 0);

      if (mode === "plan") {
        setInstallmentAmount(Number(data.installment_amount) || 0);
        setFirstAmount(Number(data.first_amount) || 0);
        setInstallmentCount(Number(data.count) || 4);
      }

      setStatus("ready");
    } catch (e) {
      setErrMsg(
        e?.response?.data?.detail ||
          "We couldn't start your checkout. Please go back and try again."
      );
      setStatus("error");
    }
  };

  const selectMode = (mode) => {
    if (mode === payMode || status === "loading") return;
    setPayMode(mode);
    initIntent(mode);
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    initIntent("full");
  }, []); // eslint-disable-line

  const applyPromo = async (e) => {
    e.preventDefault();

    setPromoError("");
    setPromoMessage("");

    const code =
      promoInput.trim().toUpperCase();

    if (!code) {
      setPromoError(
        "Enter a discount code."
      );
      return;
    }

    if (!paymentIntentId) {
      setPromoError(
        "Checkout is still loading."
      );
      return;
    }

    setApplyingPromo(true);

    if (payMode === "plan") {
      try {
        const { data } = await axios.post(
          `${API}/payments/create-installment-intent`,
          { event, items, promo_code: code }
        );
        setClientSecret(data.client_secret);
        setPaymentIntentId(data.payment_intent_id);
        setCheckoutSubtotal(Number(data.subtotal) || 0);
        setDiscount(Number(data.discount) || 0);
        setCheckoutTotal(Number(data.amount) || 0);
        setInstallmentAmount(Number(data.installment_amount) || 0);
        setFirstAmount(Number(data.first_amount) || 0);
        setInstallmentCount(Number(data.count) || 4);
        setAppliedPromo(data.promo_code || code);
        setPromoType(data.discount_type || "");
        setPromoValue(Number(data.discount_value) || 0);
        setPromoInput(data.promo_code || code);
        setPromoMessage(`${data.promo_code || code} applied`);
      } catch (e) {
        setPromoError(
          e?.response?.data?.detail ||
            "We couldn't apply that discount code."
        );
      } finally {
        setApplyingPromo(false);
      }
      return;
    }

    try {
      const { data } = await axios.post(
        `${API}/payments/apply-promo`,
        {
          payment_intent_id:
            paymentIntentId,
          code,
        }
      );

      setCheckoutSubtotal(
        Number(data.subtotal) || 0
      );

      setDiscount(
        Number(data.discount) || 0
      );

      setCheckoutTotal(
        Number(data.amount) || 0
      );

      setAppliedPromo(
        data.code || code
      );
      setPromoType(data.discount_type || "");
      setPromoValue(Number(data.discount_value) || 0);

      setPromoInput(
        data.code || code
      );

      setPromoMessage(
        `${data.code || code} applied`
      );
    } catch (e) {
      setPromoError(
        e?.response?.data?.detail ||
          "We couldn't apply that discount code."
      );
    } finally {
      setApplyingPromo(false);
    }
  };

  return (
    <div
      className="bg-[#efe6d7] min-h-screen flex flex-col"
      data-testid="checkout-view"
    >
      <Navbar />

      <section className="feral-texture flex-1 pt-28 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={onBack}
            data-testid="checkout-back"
            className="inline-flex items-center gap-1 text-[#9b3d0a] font-semibold text-sm mb-6 hover:gap-1.5 transition-all"
          >
            <ChevronLeft size={18} />
            Back to tickets
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h1 className="feral-serif text-[#9b3d0a] text-4xl mb-2">
                Checkout
              </h1>

              <p className="text-[#6e5646] mb-8">
                Complete your booking below. No account needed.
              </p>

              {allowInstallments && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="checkout-pay-mode">
                  <button
                    type="button"
                    onClick={() => selectMode("full")}
                    data-testid="pay-mode-full"
                    className={`text-left rounded-md border p-4 transition-all ${
                      payMode === "full"
                        ? "border-[#9b3d0a] bg-white ring-2 ring-[#ebc754]"
                        : "border-[#d9c3a8] bg-white/60 hover:border-[#9b3d0a]"
                    }`}
                  >
                    <p className="text-[#1e231f] font-bold text-sm">Pay in full</p>
                    <p className="text-[#6e5646] text-xs mt-1">One payment of {gbp(Number(total) || 0)}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => selectMode("plan")}
                    data-testid="pay-mode-plan"
                    className={`text-left rounded-md border p-4 transition-all ${
                      payMode === "plan"
                        ? "border-[#9b3d0a] bg-white ring-2 ring-[#ebc754]"
                        : "border-[#d9c3a8] bg-white/60 hover:border-[#9b3d0a]"
                    }`}
                  >
                    <p className="text-[#1e231f] font-bold text-sm">Pay in 4 monthly instalments</p>
                    <p className="text-[#6e5646] text-xs mt-1">
                      {payMode === "plan" && installmentAmount
                        ? `${gbp(firstAmount)} today, then 3 × ${gbp(installmentAmount)}`
                        : `Split ${gbp(Number(total) || 0)} equally over 4 months`}
                    </p>
                  </button>
                </div>
              )}

              {status === "loading" && (
                <div
                  data-testid="checkout-loading"
                  className="bg-white border border-[#d9c3a8] rounded-md p-10 flex flex-col items-center justify-center text-[#6e5646]"
                >
                  <Loader2
                    size={32}
                    className="text-[#9b3d0a] animate-spin mb-4"
                  />
                  Preparing secure checkout…
                </div>
              )}

              {status === "error" && (
                <div
                  data-testid="checkout-init-error"
                  className="bg-[#fbe9e7] border border-[#f3c2bb] rounded-md p-6 text-[#b3261e]"
                >
                  {errMsg}
                </div>
              )}

              {status === "ready" &&
                clientSecret &&
                stripePromise && (
                  <Elements
                    key={clientSecret}
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance,
                    }}
                  >
                    <PaymentForm
                      paymentIntentId={
                        paymentIntentId
                      }
                      total={
                        checkoutTotal
                      }
                      form={form}
                      onChange={setForm}
                      showReg={showReg}
                      showDietary={showDietary}
                      lines={lines}
                      bookingConfig={bookingConfig}
                      payMode={payMode}
                      firstAmount={firstAmount}
                      installmentAmount={installmentAmount}
                      installmentCount={installmentCount}
                    />
                  </Elements>
                )}
            </div>

            <aside className="lg:col-span-1">
              <div className="bg-white border border-[#d9c3a8] rounded-md p-7 shadow-sm sticky top-28">
                <h2 className="text-[#b5651d] tracking-[0.14em] text-sm font-bold uppercase mb-5">
                  Order review
                </h2>

                {eventName && (
                  <p className="text-[#1e231f] font-semibold mb-4">
                    {eventName}
                  </p>
                )}

                <p className="text-xs font-bold tracking-[0.12em] text-[#7a6a58] uppercase mb-3">
                  Items
                </p>

                <div
                  className="space-y-4"
                  data-testid="checkout-items"
                >
                  {lines.map((l, i) => (
                    <div
                      key={i}
                      className="flex justify-between gap-3"
                    >
                      <div>
                        <p className="text-[#1e231f] font-semibold text-sm leading-snug">
                          {l.qty > 1
                            ? `${l.qty}× `
                            : ""}
                          {l.name}
                        </p>

                        {(dateLine ||
                          l.meta) && (
                          <p className="text-xs text-[#9a876f] mt-0.5">
                            {l.meta ||
                              dateLine}
                          </p>
                        )}
                      </div>

                      <span className="text-[#1e231f] text-sm whitespace-nowrap">
                        {gbp(
                          l.price *
                            l.qty
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {allowPromo && (
                  <div className="border-t border-[#e0d3bf] mt-6 pt-5">
                    <p className="text-xs font-bold tracking-[0.12em] text-[#7a6a58] uppercase mb-2">
                      Discount code
                    </p>

                    <form
                      onSubmit={applyPromo}
                      className="flex gap-2"
                    >
                      <input
                        type="text"
                        value={promoInput}
                        disabled={
                          applyingPromo ||
                          Boolean(
                            appliedPromo
                          )
                        }
                        onChange={(e) => {
                          setPromoInput(
                            e.target.value
                          );
                          setPromoError("");
                          setPromoMessage("");
                        }}
                        placeholder="Enter code"
                        className="min-w-0 flex-1 bg-white border border-[#d9c3a8] px-3 py-2.5 rounded-sm outline-none focus:ring-2 focus:ring-[#ebc754] text-[#1e231f] text-sm disabled:bg-[#f4efe8]"
                      />

                      <button
                        type="submit"
                        disabled={
                          applyingPromo ||
                          Boolean(
                            appliedPromo
                          ) ||
                          status !==
                            "ready"
                        }
                        className="bg-[#9b3d0a] hover:bg-[#7e3208] disabled:opacity-50 text-[#f2e5de] px-4 py-2.5 rounded-sm text-xs font-bold tracking-[0.08em] uppercase"
                      >
                        {applyingPromo
                          ? "Applying..."
                          : appliedPromo
                          ? "Applied"
                          : "Apply"}
                      </button>
                    </form>

                    {promoError && (
                      <p className="text-xs text-[#b3261e] mt-2">
                        {promoError}
                      </p>
                    )}

                    {promoMessage && (
                      <p className="text-xs text-[#55734f] font-semibold mt-2">
                        {promoMessage}
                      </p>
                    )}
                  </div>
                )}

                <div className="border-t border-[#e0d3bf] mt-6 pt-5 space-y-2">
                  <div className="flex justify-between text-sm text-[#6e5646]">
                    <span>
                      Subtotal
                    </span>
                    <span>
                      {gbp(
                        checkoutSubtotal
                      )}
                    </span>
                  </div>

                  {discount > 0 && (
                    <div className="flex justify-between text-sm text-[#55734f] font-semibold">
                      <span>
                        {appliedPromo || "Discount"}
                        {appliedPromo && promoType === "percent" && promoValue > 0
                          ? ` (${promoValue}%)`
                          : appliedPromo && promoType === "fixed" && promoValue > 0
                          ? ` (${gbp(promoValue)} off)`
                          : ""}
                      </span>

                      <span>
                        -{gbp(discount)}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm text-[#6e5646]">
                    <span>
                      Booking fee
                    </span>
                    <span>
                      £0.00
                    </span>
                  </div>
                </div>

                <div className="border-t border-[#e0d3bf] mt-5 pt-5 flex items-end justify-between">
                  <span className="text-[#1e231f] font-bold tracking-wide uppercase text-sm">
                    Total
                  </span>

                  <span
                    className="feral-serif text-[#9b3d0a] text-3xl leading-none"
                    data-testid="checkout-total"
                  >
                    {gbp(
                      checkoutTotal
                    )}
                  </span>
                </div>

                {payMode === "plan" && installmentAmount > 0 && (
                  <div className="mt-4 bg-[#f3e7d6] border border-[#e0c9ad] rounded-sm px-4 py-3" data-testid="checkout-plan-summary">
                    <p className="text-xs font-bold tracking-[0.1em] text-[#7a6a58] uppercase mb-1">Payment plan</p>
                    <p className="text-sm text-[#5b4a37] leading-snug">
                      <strong>{gbp(firstAmount)}</strong> today, then{" "}
                      <strong>{installmentCount - 1}</strong> monthly payments of{" "}
                      <strong>{gbp(installmentAmount)}</strong>.
                    </p>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CheckoutView;